import React from 'react';
import classes from '../SingingTab.module.scss'
import { Button, TreeSelect, Radio, Tooltip, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { prepareGroups } from '../../ArgeementTab/AgreementCard';
import { ISimpleGroupForAprove, ISimpleUserForAprove } from 'interfaces';
import { InviteStatusEnum } from 'interfaces/IInvitations';
import EnterPasswordModal from '../SigningComponents/EnterPasswordModal';
import ActionButtons from '../ActionButtons/ActionButtons';
import CardTitle from '../SigningComponents/CardTitle';
import { setDocuments, setSelectedDocument } from 'store/slices/dataDocumentsSlice';
import { setSigningState } from 'store/slices/signingSlice';
import DeclineSigningModal from '../SigningComponents/DeclineSigningModal';
import convert from 'api/convert';
import dayjs from 'dayjs';
import SigningParticipants from '../SigningComponents/SigningParticipants';

type SigningCardProps = {
  closeDetailsModal: () => void;
  deleteSigning: () => Promise<void>;
  usersWithPermissions: any[];
  loadingUsersPermissions: boolean;
};

const SigningCard = ({closeDetailsModal, deleteSigning, usersWithPermissions, loadingUsersPermissions}: SigningCardProps) => {
  const [isAddingSgining, setIsAddingSgining] = React.useState(false);
  const [isPasswordSended, setIsPasswordSended] = React.useState(false);
  const [isOpenConfirmModal, setIsOpenConfirmModal] = React.useState(false);
  const [isOpenDeclineSigningModal, setIsOpenDeclineSigningModal] = React.useState(false);
  const [isQesModalOpen, setIsQesModalOpen] = React.useState(false);
  const [signatureType, setSignatureType] = React.useState<'qes' | 'ues'>('ues');
  const [usersId, setUsersId] = React.useState<string[]>([]);
  const [dataTreeSelect, setDataTreeSelect] = React.useState<any[]>([]);

  const [loadingProcess, setLoadingProcess] = React.useState({sendPass: false, decline: false, delete: false});

  const { selectedDocument, documents, dataDocumentTable, folders } = useAppSelector((state) => state.documents);
  const { users, groups, isGroupsAndUsersLoading, pendingInviteUsers } = useAppSelector((store) => store.dataUsers);
  const { userData } = useAppSelector((store) => store.userData);
  const { messageApi } = useAppSelector((store) => store.windowState);
  const { signing, needSigningFromUsers, isSigningLoading, signingInfo, mySigning } = useAppSelector((store) => store.signing);
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const locale = i18n.language || 'en';

  React.useEffect(() => {
    users[0] && setUsersId(needSigningFromUsers.map(user => user.user_id));
  }, [signing, users]);

  React.useEffect(() => {
    const usersWithSignStatus = users.map(user => ({
      ...user,
      signing_status: needSigningFromUsers.find(signUser => signUser.user_email === user.email)?.signing_status,
      document_permissions: usersWithPermissions.find(item => item.email === user.email)?.document_permissions,
      type: 'user'
    }));
    const usersWithSignStatusIds = new Set(usersWithSignStatus.map(u => u.id));

    const invitesAsUsers = pendingInviteUsers
      .filter(invite => invite.invite_status === InviteStatusEnum.PENDING && !invite.is_parent_invite && !usersWithSignStatusIds.has(invite.id))
      .map((invite) => ({
        id: invite.user_id,
        first_name: '',
        last_name: '',
        email: invite.email,
        phone_number: '',
        group: [invite.group_id],
        type: 'invite',
      }));

    const allUsers = [...usersWithSignStatus, ...invitesAsUsers];
    const treeData = groups.map(group => prepareGroups(group, allUsers, t, 'can_manage_signing'));
    setDataTreeSelect(treeData);
  }, [users, usersWithPermissions, pendingInviteUsers, locale]);

  const onOpenDeclineModal = () => setIsOpenDeclineSigningModal(true);

  const openQesModal = () => setIsQesModalOpen(true);
  
  const closeQesModal = () => setIsQesModalOpen(false);

  const handleQesSigningSuccess = async () => {
    try {
      updateSigningStateAfterSign();
    } catch (e) {
      console.error('Error updating signing state after QES signing:', e);
    }
  };

  const tProps = {
    treeData: dataTreeSelect,
    value: usersId,
    onChange: (newValue: string[]) => setUsersId(newValue),
    treeCheckable: true,
    showCheckedStrategy: TreeSelect.SHOW_CHILD,
    placeholder: t('Documents.details.signing.treePlaceholder'),
  };

  const documentPermissions = dataDocumentTable.find(({ id }) => id === selectedDocument?.id)?.permissions!;

  const updateSigningStateAfterSign = () => {
    const newMySigning = mySigning
      ? {...mySigning, signing_status: !mySigning.signing_status, signing_date: dayjs().utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSS') }
      : null

    const newNeedSigningFromUsers = needSigningFromUsers.map(signingFromUser => 
      (signingFromUser.user_id === newMySigning?.user_id ? newMySigning : signingFromUser)
    );

    const newSigning = signing.map(signItem => ({
      ...signItem,
      users: signItem.users?.map(user => user.user_id === newMySigning?.user_id ? newMySigning : user)
    }))

    dispatch(setSigningState({
      mySigning: newMySigning,
      needSigningFromUsers: newNeedSigningFromUsers,
      signing: newSigning,
    }));

    const statusesWithoutTrue = newSigning.map(group => {
      return group.users?.map(user => String(user.signing_status));
    })
    .flat()
    .filter(elem => elem !== 'true');

    dispatch(
      setDocuments({
        documents: documents.map((document) => (document.id === selectedDocument!.id
          ? {
            ...document,
            signing_status: statusesWithoutTrue[0] ? 0 : 2,
            permissions: documentPermissions,
          } : document
        )),
        folders,
      })
    );
    
    dispatch(setSelectedDocument({...selectedDocument!, signing_status: statusesWithoutTrue[0] ? 0 : 2}));
  };

  const sendRequestForApproval = async () => {
    const allUsers = [
      ...users.map(user => ({ ...user, type: 'user' })),
      ...pendingInviteUsers
        .filter(invite => invite.invite_status === InviteStatusEnum.PENDING)
        .map(invite => ({
          id: invite.user_id,
          first_name: '',
          last_name: '',
          email: invite.email,
          phone_number: '',
          group: [invite.group_id],
          type: 'invite'
        }))
    ];

      const choosenUsers = allUsers.filter(user => usersId.includes(user.id)).map(user => {
      return {
        user_id: user.id,
        user_name: user.first_name,
        user_email: user.email,
        signing_status: null,  // targetUserState || null,
        parent_group_id: user.group[0]
      }
    });
    
    const simpleGroups: ISimpleGroupForAprove[] = groups.map(group => ({group_id: group.id, group_name: group.name}));
    
    const signing_state = choosenUsers.reduce((acc: ISimpleGroupForAprove[], currentUser: ISimpleUserForAprove) => {
      const targetGroup = simpleGroups.find(group => group.group_id === currentUser.parent_group_id);
      const theSameGroupInAcc = acc.find((group => group.group_id === targetGroup?.group_id));
      
      delete currentUser.parent_group_id;
      
      theSameGroupInAcc
        ? acc = acc.map((group: any) => (group.group_id === targetGroup?.group_id ? {...group, users: [...group.users, currentUser]} : group))
        : acc.push({...targetGroup!, users: [currentUser]})
      
      return acc;
    }, []);

    setIsAddingSgining(true);

    try {
      await convert.addSigning({
        document_id: selectedDocument?.id!,
        signing_state,
        signature_type: signatureType
      });
      
      dispatch(setDocuments(
        {
          documents: documents.map(document => {
            return document.id === selectedDocument!.id
            ? { ...document, signing_status: 0, permissions: documentPermissions, status: 0 }
            : document
          }),
          folders
        }
      ))
      dispatch(setSelectedDocument({...selectedDocument!, signing_status: 0}));
      messageApi?.success(t('Documents.details.signing.sendForSigning'));
      closeDetailsModal();
    } catch (e) {
      messageApi?.error(t('Documents.details.signing.sendForSigningErr'));
    } finally {
      setIsAddingSgining(false);
    }
  };

  const getСonfirmationPassword = async () => {
    setLoadingProcess({...loadingProcess, sendPass: true});
    try {
      await convert.requestSigning({document_id: selectedDocument?.id!});
      messageApi?.success(`${t('Documents.details.signing.successConfirmPassword')}: ${userData?.email}`, 10);
      setIsPasswordSended(true);
      setIsOpenConfirmModal(true);
    } catch (e) {
      messageApi?.error(t('Documents.details.signing.errorConfirmPassword'))
    } finally {
      setLoadingProcess({...loadingProcess, sendPass: false});
    }
  }

  const signDocument = async (password: string) => {
    setIsAddingSgining(true);
    try {
      await convert.changeSigntStatus({document_id: signingInfo?.id!, sign_password:password});
      updateSigningStateAfterSign();
      messageApi?.success(t('Documents.details.signing.signedSuccessfully'));
    } catch (e) {
      messageApi?.error(t('Documents.details.signing.signingError'))
    } finally {
      setIsAddingSgining(false);
    }
  };

  const deleteCurrentSigning = async () => {
    setLoadingProcess({...loadingProcess, delete: true});
    await deleteSigning()
    setLoadingProcess({...loadingProcess, delete: false});
  };

  return (
    <div>
      {userData?.has_sign ? (
        <div className={classes.signingCard}>
          <CardTitle signingCreated={Boolean(signing[0])} />

          {!Boolean(signing[0]) && (
            <Radio.Group
              onChange={(e) => setSignatureType(e.target.value)}
              value={signatureType}
              className={classes.signatureTypeRadio}
              disabled={isAddingSgining}
            >
              <Tooltip
                title={t("Documents.details.signing.signatureTypeUesTooltip")}
                placement="left"
              >
                <Radio
                  value="ues"
                  style={{ display: "block", marginBottom: 8 }}
                >
                  {t("Documents.details.signing.signatureTypeUes")}
                </Radio>
              </Tooltip>
              {/* QES/КЭП временно отключено (оставляем только ПЭП) */}
              {/* <Tooltip  title={t('Documents.details.signing.signatureTypeQesTooltip')} placement="left">
                <Radio value="qes" style={{ display: 'block', marginBottom: 8 }}>
                  {t('Documents.details.signing.signatureTypeQes')}
                </Radio>
<<<<<<< HEAD
              </Tooltip>
=======
>>>>>>> test
              </Tooltip> */}
            </Radio.Group>
          )}

          <TreeSelect
            disabled={
              isGroupsAndUsersLoading ||
              loadingUsersPermissions ||
              isAddingSgining ||
              isSigningLoading ||
              Boolean(signing[0]) ||
              isPasswordSended
            }
            loading={
              isGroupsAndUsersLoading ||
              loadingUsersPermissions ||
              isSigningLoading ||
              isAddingSgining
            }
            className={classes.singingTree}
            placement="topLeft"
            {...tProps}
          />

          <ActionButtons
            isSigningCreated={Boolean(signing[0])}
            isPasswordSended={isPasswordSended}
            setIsOpenConfirmModal={setIsOpenConfirmModal}
            isAddingSgining={isAddingSgining}
            usersId={usersId}
            sendRequestForApproval={sendRequestForApproval}
            getСonfirmationPassword={getСonfirmationPassword}
            loadingProcess={loadingProcess}
            deleteSigning={deleteCurrentSigning}
            onOpenDeclineModal={onOpenDeclineModal}
            signatureType={signingInfo?.signature_type}
            onOpenQesModal={openQesModal}
          />

          {Boolean(signing[0]) && (
            <>
              <Divider style={{ margin: "15px 0" }} />
              <SigningParticipants
                usersWithPermissions={usersWithPermissions}
                loadingUsersPermissions={loadingUsersPermissions}
              />
            </>
          )}

          {isOpenConfirmModal && (
            <React.Suspense fallback={<div />}>
              <EnterPasswordModal
                open={isOpenConfirmModal}
                onCancel={() => setIsOpenConfirmModal(false)}
                signDocument={signDocument}
                isAddingSgining={isAddingSgining}
              />
            </React.Suspense>
          )}

          {isOpenDeclineSigningModal && (
            <React.Suspense fallback={<div />}>
              <DeclineSigningModal
                open={isOpenDeclineSigningModal}
                onCancel={() => setIsOpenDeclineSigningModal(false)}
                documentId={selectedDocument?.id!}
                documentPermissions={documentPermissions}
              />
            </React.Suspense>
          )}

          {/* {isQesModalOpen && (
            <React.Suspense fallback={<div />}>
              <QesSigningModal
                open={isQesModalOpen}
                onCancel={closeQesModal}
                documentId={selectedDocument?.id!}
                onSuccess={handleQesSigningSuccess}
                selectedDocument={selectedDocument!}
              />
            </React.Suspense>
          )} */}
        </div>
      ) : (
        <div className={classes.needToAcceptRules}>
          {t("Documents.details.signing.needToAcceptRules")}
        </div>
      )}
    </div>
  );
}

export default SigningCard;
